import { Badge, Checkbox } from "@xola/ui-kit";
import clsx from "clsx";
import { isExperienceProduct, isScheduled } from "common";
import { sortBy } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ALL_PRODUCT_OBJECTS, Product, ProductObject, productTitleMap, SelectionState } from "types";
import { useDebounce } from "use-debounce";
import { Loader } from "../Loader";
import { NoResults } from "./NoResults";
import { NoTranslate } from "./NoTranslate";
import { ProductImage } from "./ProductImage";
import { getProductObjectIcon, ProductObjectFilterDropdown } from "./ProductObjectFilter";
import type { ProductObjectFilter } from "./ProductObjectFilter";
import { SearchInput } from "./SearchInput";
import { useProducts } from "@/hooks/resources/useProduct";
import { useSelection } from "@/hooks/useSelection";
import { isExperiencePublished } from "@/models/Experience";

export interface ProductOrCatalogSelectorProps {
    value: SelectionState;
    onChange: (value: SelectionState) => void;
    productObjects?: ProductObject[];
    defaultEnabledProductObjects?: ProductObject[];
    className?: string;
    classNames?: { list?: string };
    enableSelectAll?: boolean;
    hideDraft?: boolean;
    hideUnScheduled?: boolean;
}

const defaultValue: SelectionState = {
    all: false,
    selected: [],
};

export const ProductOrCatalogSelector = ({
    value = defaultValue,
    productObjects,
    defaultEnabledProductObjects,
    enableSelectAll = true,
    hideDraft = false,
    hideUnScheduled = false,
    className,
    classNames,
    onChange,
}: ProductOrCatalogSelectorProps) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
    const [productObjectFilters, setProductObjectFilters] = useState<ProductObjectFilter[]>([]);

    const { products, isLoading, isFetchingNextPage } = useProducts();
    const isLoadingProducts = isLoading || isFetchingNextPage;

    // Filter products based on productObject and visibility filters
    const filteredVisibleProducts = useMemo(() => {
        return products.filter((product) => {
            // Apply product object filter
            if (productObjects && productObjects.length > 0 && !productObjects.includes(product.object)) {
                return false;
            }

            // Apply draft and schedule filters
            if (isExperienceProduct(product)) {
                if (hideDraft && !isExperiencePublished(product)) {
                    return false;
                }

                if (hideUnScheduled && !isScheduled(product)) {
                    return false;
                }
            }

            return true;
        });
    }, [products, productObjects, hideDraft, hideUnScheduled]);

    // Initialize product type filters with counts
    useEffect(() => {
        const productObjectCountMap = new Map<ProductObject, number>();

        for (const product of filteredVisibleProducts) {
            const count = productObjectCountMap.get(product.object) ?? 0;
            productObjectCountMap.set(product.object, count + 1);
        }

        const filters: ProductObjectFilter[] = [];
        for (const productObject of ALL_PRODUCT_OBJECTS) {
            const count = productObjectCountMap.get(productObject) ?? 0;
            if (count > 0) {
                // Determine if this type should be enabled by default
                const enabled = defaultEnabledProductObjects?.includes(productObject) ?? true;

                filters.push({
                    productObject: productObject,
                    label: productTitleMap[productObject],
                    count,
                    enabled,
                });
            }
        }

        setProductObjectFilters(filters);
    }, [filteredVisibleProducts, defaultEnabledProductObjects]);

    // Apply product object filters
    const filteredByObject = useMemo(() => {
        // Apply product object filters, selected by user
        const selectedProductObjects = productObjectFilters
            .filter((filter) => filter.enabled)
            .map((filter) => filter.productObject);
        if (selectedProductObjects.length === 0) {
            return [];
        }

        // Only filter if not all types are selected to skip unnecessary filtering
        if (productObjectFilters.length > 0 && selectedProductObjects.length !== productObjectFilters.length) {
            return filteredVisibleProducts.filter((product) => selectedProductObjects.includes(product.object));
        }

        return filteredVisibleProducts;
    }, [filteredVisibleProducts, productObjectFilters]);

    // Apply search filter on top of type-filtered products
    const filteredProducts = useMemo(() => {
        if (!debouncedSearchTerm) {
            return filteredByObject;
        }

        const term = debouncedSearchTerm.trim().toLowerCase();
        return filteredByObject.filter((product) => product.name.toLowerCase().includes(term));
    }, [filteredByObject, debouncedSearchTerm]);

    const handleSelectionChange = useCallback(
        (newState: SelectionState) => {
            // Get IDs of products that are NOT currently visible (filtered out)
            const filteredProductIds = new Set(filteredProducts.map((product) => product.id));
            const hiddenSelectedIds = value.selected.filter((id) => !filteredProductIds.has(id));

            // Compute selected IDs from visible/filtered products
            const visibleSelectedIds = newState.all ? filteredProducts.map((product) => product.id) : newState.selected;

            // Combine visible selections with hidden selections to preserve them
            const finalSelectedIds = [...new Set([...visibleSelectedIds, ...hiddenSelectedIds])];

            onChange({ all: false, selected: finalSelectedIds });
        },
        [filteredProducts, value.selected, onChange],
    );

    const defaultSelection = useMemo(() => ({ all: value.all, selected: value.selected }), [value]);
    const selection = useSelection(filteredProducts, false, defaultSelection, false, handleSelectionChange);

    // Update product object filter counts based on SELECTED products
    const productObjectFiltersWithCounts = useMemo(() => {
        const productObjectCountMap = new Map<ProductObject, number>();

        // Get all selected products from the filtered list (not the full products list)
        const selectedProducts = filteredVisibleProducts.filter(
            (product) => selection.state.all || selection.state.selected.includes(product.id),
        );

        // Count selected products by type
        for (const product of selectedProducts) {
            const count = productObjectCountMap.get(product.object) ?? 0;
            productObjectCountMap.set(product.object, count + 1);
        }

        return productObjectFilters.map((filter) => ({
            ...filter,
            count: productObjectCountMap.get(filter.productObject) ?? 0,
        }));
    }, [productObjectFilters, filteredVisibleProducts, selection.state]);

    const handleSearchChange = (searchResult: string) => {
        setSearchTerm(searchResult.trim());
    };

    const handleProductObjectFiltersChange = (newFilters: ProductObjectFilter[]) => {
        const alreadySelectedIds =
            selection.state.selected.length > 0 ? selection.state.selected : selection.state.storedIds ?? [];
        selection.setState({ all: false, selected: alreadySelectedIds });
        setProductObjectFilters(newFilters);
    };

    const handleSelectAll = () => {
        if (selection.isSelectedAll) {
            // Get IDs of products that are NOT currently visible (filtered out)
            const filteredProductIds = new Set(filteredProducts.map((product) => product.id));
            const hiddenSelectedIds = selection.state.selected.filter((id) => !filteredProductIds.has(id));

            // Deselect all visible items, keep hidden ones
            selection.setState({ all: false, selected: hiddenSelectedIds });
            onChange({ all: false, selected: hiddenSelectedIds });
        } else {
            const alreadySelectedHiddenIds =
                selection.state.selected.length > 0 ? selection.state.selected : selection.state.storedIds ?? [];
            // Select all visible items + keep hidden ones
            const visibleSelectedIds = filteredProducts.map((product) => product.id);
            const finalSelectedIds = [...new Set([...visibleSelectedIds, ...alreadySelectedHiddenIds])];
            selection.setState({ all: true, selected: finalSelectedIds });
            onChange({ all: true, selected: finalSelectedIds });
        }
    };

    return (
        <div className={clsx("ui-product-selector overflow-hidden rounded-lg border border-gray-lighter", className)}>
            <div className="space-y-3 border-b border-b-gray-lighter bg-white p-3">
                <ProductObjectFilterDropdown
                    productObjectFilters={productObjectFiltersWithCounts}
                    onProductObjectFiltersChange={handleProductObjectFiltersChange}
                />

                <SearchInput className="w-full" value={searchTerm} onChange={handleSearchChange} />
            </div>

            {enableSelectAll ? (
                <div className="flex items-center justify-between bg-gray-lighter py-2 px-3">
                    <div className="flex items-center gap-1.5">
                        <Checkbox
                            disabled={isLoadingProducts || filteredProducts.length === 0}
                            checked={selection.isSelectedAll}
                            classNames={{
                                label: "font-bold tracking-tightest !text-black",
                            }}
                            label="Select all"
                            data-testid="product-catalog-selector-select-all-checkbox"
                            onChange={handleSelectAll}
                        />
                        {isLoadingProducts ? <Loader size="xs" className="!p-0" /> : null}
                    </div>

                    <Badge className="text-gray-darker">
                        {selection.count === "All" ? (
                            <span>All Selected</span>
                        ) : (
                            <span>
                                <var data-var="selectionCount">{selection.count}</var> Selected
                            </span>
                        )}
                    </Badge>
                </div>
            ) : null}

            <div
                className={clsx(
                    "h-60 divide-y divide-gray-light overflow-y-auto overflow-x-hidden bg-white",
                    classNames?.list,
                )}
            >
                {filteredProducts.length === 0 ? (
                    <NoResults>No results found</NoResults>
                ) : (
                    sortBy(filteredProducts, "name").map((product: Product) => (
                        <ProductCheckbox key={product.id} selection={selection} product={product} />
                    ))
                )}
            </div>
        </div>
    );
};

interface ProductCheckboxProps {
    selection: ReturnType<typeof useSelection>;
    product: Product;
}

const ProductCheckbox = ({ selection, product }: ProductCheckboxProps) => {
    const handleCheckboxChange = (product: Product) => {
        selection.select(product);
    };

    return (
        <Checkbox
            key={product.id}
            checked={selection.isSelected(product)}
            name={product.id}
            label={<ProductCheckboxContent product={product} icon={getProductObjectIcon(product.object)} />}
            className={clsx(
                "px-3 py-2 hover:cursor-pointer",
                selection.isSelected(product) ? "bg-blue-lighter" : "hover:bg-gray-hover",
            )}
            classNames={{ checkbox: "!border !border-gray" }}
            onChange={() => handleCheckboxChange(product)}
        />
    );
};

const ProductCheckboxContent = ({ product, icon }: { product: Product; icon: React.ReactNode }) => {
    return (
        <span data-id={product.id} className="flex items-center hover:cursor-pointer">
            <ProductImage className="mr-2" product={product} size="tiny" />
            <span className="mr-2">{icon}</span>
            <NoTranslate className="text-black">{product.name.trim()}</NoTranslate>
        </span>
    );
};
